/**
 * Part Orders — Phase 1 contracts.
 *
 * See sm4rt-api/docs/architecture/part-orders-module.md for the
 * full design rationale. Lifecycle: a PartOrderLine is created when a quote
 * is paid (or pushed via manual override). It sits in a per-vendor basket
 * with status `to_order`. When the manager places an order with the vendor,
 * one or more lines are bundled under a PartOrder header and flipped to
 * `ordered`. Receipts roll status forward to `partially_received` /
 * `received`. Cancellation paths preserve audit history (see
 * `cancellationReason`).
 */

export type PartOrderLineStatus =
  | 'to_order'
  | 'ordered'
  | 'partially_received'
  | 'received'
  | 'cancelled';

export type PartOrderLineCancellationReason =
  | 'vendor_committed'   // PO sent, vendor must be contacted to cancel
  | 'after_receipt';     // parts already arrived; flagged for RTV/sell-on

export type PartOrderTriggerSource =
  | 'invoice_payment'    // default — fired by the paid-invoice hook
  | 'manual_quote'       // pushed early from the quote detail page
  | 'manual_invoice'     // pushed before payment from the invoice detail page
  | 'stock'              // free-standing stock order — no quote/workitem
  | 'internal_use';      // free-standing order for internal company use

/**
 * Free-standing line kinds — those with no quote/workitem source. The trigger
 * source values that an operator can pick from in the "Add line" dialog.
 */
export type FreestandingLineKind = 'stock' | 'internal_use';

/**
 * How the parts on a placed PO reach us.
 *
 *  - `delivery` — the vendor ships the parts (the default; freight may apply
 *    via `shippingCostExGst`).
 *  - `pickup`   — we collect from the vendor's trade counter / warehouse in
 *    person. Pickup orders carry no freight, so `shippingCostExGst` is forced
 *    to 0, and `expectedDeliveryDate` is read as the ready-for-collection date.
 */
export type PartOrderFulfillmentMethod = 'delivery' | 'pickup';

export const ALL_PART_ORDER_LINE_STATUSES: readonly PartOrderLineStatus[] = [
  'to_order',
  'ordered',
  'partially_received',
  'received',
  'cancelled',
] as const;

/**
 * A single part needing to be ordered (or that has been ordered / received).
 *
 * Most lines trace back to a `QuoteOption` that had a `sourceDistiId` — that's
 * how the vendor + cost snapshot are populated. Hand-typed quote options
 * (no `sourceDistiId`) are never created as PartOrderLines.
 *
 * Free-standing lines (triggeredBy === 'stock' or 'internal_use') have no
 * quote/quoteItem/quoteOption/workitem and are created directly via
 * `POST /api/part_order_lines`. Stock lines are for resale inventory;
 * internal-use lines are for parts the business buys for its own use.
 *
 * The fields prefixed "joined on read" come from `$expand` on the related
 * `cr001_part_orders` header — they are absent on lines still in basket
 * (status = 'to_order'), since those lines have no PO yet.
 */
export interface PartOrderLine {
  id: string;
  workitemId: string | null;    // null when the source quote isn't tied to a ticket/job, or when this is a stock line
  quoteId: string | null;       // null for stock lines
  quoteItemId: string | null;   // null for stock lines
  quoteOptionId: string | null; // null for stock lines
  vendorId: string;
  vendorName: string;       // joined from cr001_vendorses on read
  /** Source quote number, joined on read. Null for stock lines. */
  quoteNumber: string | null;
  /** Customer (business or full) name on the source quote, joined on read. Null for stock lines. */
  customerName: string | null;
  /** Customer mobile (falls back to phone), joined from the source quote's customer. Null for stock lines. For the part-receipt customer label. */
  customerMobile: string | null;
  /** Customer email, joined from the source quote's customer. Null for stock lines. For the part-receipt customer label. */
  customerEmail: string | null;
  sku: string;
  description: string;       // snapshot at trigger time
  qty: number;
  expectedUnitCost: number;  // snapshot from QuoteOption.buyPriceExGst
  actualUnitCost: number | null;
  productUrl: string | null; // optional product page link — snapshot of the source QuoteOption.url / component.url, or a stock line's manual link
  status: PartOrderLineStatus;
  cancellationReason: PartOrderLineCancellationReason | null;
  partOrderId: string | null;
  receivedQty: number;
  receivedDate: string | null;
  triggeredBy: PartOrderTriggerSource;
  createdAt: string;

  /** Upper-bound lead time in calendar days, snapshotted from the source
   *  quote's `RepairServiceComponent.leadTime` at send-to-ordering time
   *  (e.g. "2-3 weeks" → 21). Null when the source line carried no lead time.
   *  Drives `expectedReceiptDate` at place-order time. */
  leadTimeDays: number | null;
  /** Per-line expected receipt date, computed at place-order as
   *  `orderDate + leadTimeDays` (calendar days). Distinct from the PO-header
   *  `expectedDeliveryDate` (a single ETA for the whole PO). Null until the
   *  line is ordered, or when it carried no lead time. */
  expectedReceiptDate: string | null;

  // Joined on read from the related cr001_part_orders header (when partOrderId set)
  orderDate: string | null;
  expectedDeliveryDate: string | null;
  /** How the parent PO is fulfilled — `'delivery'` or `'pickup'`, joined on
   *  read from the PO header. Null for basket lines that have no PO yet.
   *  When `'pickup'`, `expectedDeliveryDate` is the ready-for-collection date. */
  fulfillmentMethod: PartOrderFulfillmentMethod | null;
  poNumber: string | null;
  /** Vendor's own order/reference number, joined from the PO header. */
  vendorOrderNumber: string | null;
  notes: string | null;
}

/**
 * Header for a placed vendor purchase order. Created when the manager
 * bundles one or more basket lines into a real vendor order via
 * `POST /api/part_orders`. Lines all share the same `vendorId`.
 *
 * The header does not persist its own status — display status is derived
 * from the lines on read (see part-orders-module.md "Header state").
 */
export interface PartOrder {
  id: string;
  vendorId: string;
  vendorName: string;       // joined from cr001_vendorses on read
  poNumber: string;
  /**
   * Number/reference the vendor returns when we place the order (their
   * sales-order number, web order ID, quote ID confirmed-to-order, etc.).
   * Captured at place-order time when known and editable post-placement
   * via PATCH. Null when not yet supplied. Stored in
   * `cr001_vendor_order_number`.
   */
  vendorOrderNumber: string | null;
  orderDate: string;
  expectedDeliveryDate: string | null;
  /**
   * How the parts are obtained from the vendor — `'delivery'` (the vendor
   * ships them, the default) or `'pickup'` (we collect in person). Pickup
   * orders carry no freight (`shippingCostExGst === 0`) and read
   * `expectedDeliveryDate` as the ready-for-collection date. Stored in
   * `cr001_fulfillment_method`; defaults to `'delivery'` for legacy rows
   * pre-dating the field.
   */
  fulfillmentMethod: PartOrderFulfillmentMethod;
  notes: string | null;
  shippingCostExGst: number; // cr001_shipping; 0 if no shipping was charged
  /**
   * Disti payment-processing surcharge captured at place-order time and
   * reconcilable post-placement when the disti invoice lands. Some
   * distributors (esp. card-on-file payment) charge a percent-of-sale
   * surcharge that we absorb on procurement. 0 when the disti doesn't
   * charge one. Stored ex-GST in `cr001_transaction_fee`.
   */
  transactionFeeExGst: number;
  orderedBy: string;        // cr001_userses GUID
  orderedByName: string;    // joined display name
  createdAt: string;
  lines: PartOrderLine[];   // hydrated on the single-PO detail read
}

// ---------------------------------------------------------------------------
// Request bodies for mutating endpoints
// ---------------------------------------------------------------------------

/**
 * Body for POST /api/part_orders/send_from_quote.
 * Used by the manual-override flow on the quote detail page.
 *
 * Two source shapes are supported:
 *   - `quote_item_ids` — legacy items+options tree (new-computer-style
 *     quotes, incl. custom builds). The backend resolves each item's
 *     `selectedOptionId` and reads cost off the option snapshot. The
 *     vendor comes from the option's `sourceDistiId` (disti-sourced) or,
 *     when absent (hand-typed / retail / inventory parts), from
 *     `quote_item_vendors` mapping item id → vendor id.
 *   - `repair_component_ids` — repair-quote part components. These
 *     don't carry an embedded vendor, so the caller must supply
 *     `repair_component_vendors` mapping each component id to the
 *     vendor the part will actually be ordered from. The backend
 *     reads description, sku, qty and ex-GST cost off the component.
 *
 * The two arrays are independent and can be sent together; the
 * response's `skipped` entries identify the offending line via the
 * same id field (`quoteItemId` — used as a generic line-id here).
 */
export interface SendFromQuoteRequest {
  quote_id: string;
  quote_item_ids?: string[];
  /**
   * Vendor override for item lines whose selected option has no
   * `sourceDistiId` (hand-typed / retail / inventory parts, e.g. custom
   * build components). Maps item id → vendor id (Dataverse
   * `cr001_vendorses` GUID). Disti-sourced options ignore this (their
   * snapshot wins). An item with neither is skipped with reason
   * `"no vendor selected"`.
   */
  quote_item_vendors?: Record<string, string>;
  repair_component_ids?: string[];
  /**
   * Required when `repair_component_ids` is non-empty. Maps each
   * component id → vendor id (Dataverse `cr001_vendorses` GUID). Any
   * component without an entry is skipped with reason
   * `"vendor not selected"`.
   */
  repair_component_vendors?: Record<string, string>;
}

/**
 * Body for POST /api/part_order_lines — create a free-standing line directly,
 * unrelated to any quote/workitem. The line lands in the basket
 * (status = 'to_order') and is bundled into a PO via the normal flow.
 *
 * `kind` selects between resale stock and parts for internal company use;
 * it is persisted on the line via `triggeredBy`. Defaults to `'stock'` for
 * backwards compatibility with the original endpoint contract.
 */
export interface CreateStockLineRequest {
  vendor_id: string;
  sku: string;
  description: string;
  qty: number;
  expected_unit_cost: number;      // ex-GST
  product_url?: string;            // link to the vendor product page
  kind?: FreestandingLineKind;     // 'stock' (default) | 'internal_use'
}

/**
 * Body for POST /api/part_orders/send_from_invoice.
 * Only valid when the invoice was raised from a quote.
 */
export interface SendFromInvoiceRequest {
  invoice_id: string;
  line_item_ids: string[];
}

/**
 * Response shape for the two send_from_* endpoints. `skipped` carries the
 * reason for each ineligible line (no source vendor, already in basket,
 * quote in wrong status, etc.).
 */
export interface SendToBasketResponse {
  created: PartOrderLine[];
  skipped: Array<{ quoteItemId: string; reason: string }>;
}

/**
 * Body for POST /api/part_orders — manager places the vendor order.
 * `actual_unit_costs` is optional; lines without a per-line entry keep their
 * `expectedUnitCost` until receipt.
 */
export interface PlaceOrderRequest {
  vendor_id: string;
  line_ids: string[];
  po_number: string;
  vendor_order_number?: string | null;
  order_date: string;                       // ISO date
  expected_delivery_date?: string | null;
  /**
   * Fulfillment method for the PO — `'delivery'` (default when omitted) or
   * `'pickup'`. When `'pickup'`, the UI forces `shipping_cost_ex_gst` to 0
   * and `expected_delivery_date` is the ready-for-collection date.
   */
  fulfillment_method?: PartOrderFulfillmentMethod;
  notes?: string | null;
  actual_unit_costs?: Record<string, number>;   // line_id -> cost (ex GST)
  /**
   * Per-line quantity override applied when the order is placed — lets the
   * buyer adjust qty against the vendor's pack/MOQ before committing the PO
   * (e.g. order 75 when the quote said 70). Omitted lines keep their basket
   * qty. Values are positive integers.
   */
  order_qtys?: Record<string, number>;          // line_id -> qty
  shipping_cost_ex_gst?: number;            // freight charged on this PO, ex GST
  /**
   * Disti payment-processing surcharge captured at the time of placing
   * the order, ex GST. Reconciled later via PATCH when the disti invoice
   * lands and the actual fee is known.
   */
  transaction_fee_ex_gst?: number;
}

/**
 * Body for PATCH /api/part_orders/{id} — edit header fields after placement.
 * Restructuring lines (qty/sku changes) requires cancel + recreate.
 */
export interface UpdatePartOrderRequest {
  po_number?: string;
  vendor_order_number?: string | null;
  expected_delivery_date?: string | null;
  /**
   * Switch the PO between `'delivery'` and `'pickup'`. When set to `'pickup'`
   * the caller should also send `shipping_cost_ex_gst: 0` (the edit UI does),
   * since pickup carries no freight.
   */
  fulfillment_method?: PartOrderFulfillmentMethod;
  notes?: string | null;
  shipping_cost_ex_gst?: number;
  transaction_fee_ex_gst?: number;
}

/**
 * Body for PATCH /api/part_order_lines/{id}/receive.
 * Service auto-rolls line status to partially_received / received based on
 * cumulative receivedQty vs qty.
 */
export interface ReceiveLineRequest {
  received_qty: number;
  received_date?: string;       // defaults to now
  actual_unit_cost?: number;    // captured on receipt if not already set at order time
  /**
   * For resale-license SKU receipts: the redemption keys to mint into license
   * inventory, one per unit received. When present, the count MUST equal
   * `received_qty`; each becomes an `available` LicenseKey linked to this line.
   * Omit for ordinary (non-license) receipts. See licenseKey.ts.
   */
  license_keys?: string[];
  /**
   * How many repair-ticket labels to print for the received units — one per
   * unit, to attach to the arriving parts. Defaults to 0 (no printing) when
   * omitted; the receive dialog pre-fills `min(received_qty, 10)` and the API
   * rejects values outside 0..10. Ignored for stock / internal-use lines (they
   * have no customer ticket to label).
   */
  label_count?: number;
}

/**
 * Body for PATCH /api/part_order_lines/{id}/cancel. The service decides
 * which terminal state the line moves to (or hard-deletes pre-PO lines)
 * based on the current status.
 */
export interface CancelLineRequest {
  // Reserved for future explicit-reason overrides; today the reason is
  // derived from the line's current status (to_order -> deleted;
  // ordered/partially_received -> vendor_committed; received -> after_receipt).
}
